import { gql } from '../../../core/graphql-client.js';
import { toast } from '../../../core/event-bus.js';
import type { Role } from '../../../types.js';

interface RoleCompat {
  roleSlug1: string;
  roleSlug2: string;
}

export class RolesPage extends HTMLElement {
  private roles: Role[] = [];
  private compatibilities: RoleCompat[] = [];

  connectedCallback(): void {
    this.innerHTML = '<loading-spinner text="Ładowanie ról..."></loading-spinner>';
    this.loadData();
  }

  private async loadData(): Promise<void> {
    try {
      const [rData, cData] = await Promise.all([
        gql.query<{ roles: Role[] }>(`
          query { roles { slug displayName timeSlot isAutoScheduled sortOrder } }
        `),
        gql.query<{ roleCompatibilities: RoleCompat[] }>(`
          query { roleCompatibilities { roleSlug1 roleSlug2 } }
        `),
      ]);

      this.roles = rData.roles.sort((a, b) => a.sortOrder - b.sortOrder);
      this.compatibilities = cData.roleCompatibilities;
      this.render();
    } catch (e) {
      this.innerHTML = `<div class="page"><p class="error-text">Błąd: ${e instanceof Error ? e.message : e}</p></div>`;
    }
  }

  private isCompatible(slug1: string, slug2: string): boolean {
    const [s1, s2] = [slug1, slug2].sort();
    return this.compatibilities.some(c => c.roleSlug1 === s1 && c.roleSlug2 === s2);
  }

  private render(): void {
    const autoRoles = this.roles.filter(r => r.isAutoScheduled);

    this.innerHTML = `
      <div class="page">
        <div class="page-header">
          <h1>Role</h1>
        </div>

        <div class="card">
          <h3 class="section-title">Lista ról</h3>
          <table class="roles-table">
            <thead>
              <tr>
                <th>Nazwa</th>
                <th>Slot czasowy</th>
                <th>Auto</th>
              </tr>
            </thead>
            <tbody>
              ${this.roles.map(r => `
                <tr>
                  <td>${r.displayName}</td>
                  <td class="slot-cell">${this.slotLabel(r.timeSlot)}</td>
                  <td>
                    <label class="switch">
                      <input type="checkbox" class="auto-toggle" data-role-slug="${r.slug}"
                             ${r.isAutoScheduled ? 'checked' : ''}>
                      <span></span>
                    </label>
                  </td>
                </tr>
              `).join('')}
            </tbody>
          </table>
        </div>

        <div class="card">
          <h3 class="section-title">Macierz kompatybilności</h3>
          <p class="matrix-hint">Zaznaczone = mogą współistnieć (ta sama osoba w tym samym dniu). Odznaczone = wykluczają się.</p>
          <div class="matrix-wrap">
            <table class="compat-matrix">
              <thead>
                <tr>
                  <th></th>
                  ${autoRoles.map(r => `<th class="matrix-header" title="${r.displayName}">${this.shortName(r.displayName)}</th>`).join('')}
                </tr>
              </thead>
              <tbody>
                ${autoRoles.map((r1, i) => `
                  <tr>
                    <td class="matrix-row-header">${r1.displayName}</td>
                    ${autoRoles.map((r2, j) => {
                      if (i === j) return '<td class="matrix-self">—</td>';
                      if (j < i) return '<td class="matrix-mirror"></td>';
                      const checked = this.isCompatible(r1.slug, r2.slug);
                      return `
                        <td class="matrix-cell">
                          <input type="checkbox" class="compat-check"
                                 data-r1="${r1.slug}" data-r2="${r2.slug}"
                                 ${checked ? 'checked' : ''}>
                        </td>
                      `;
                    }).join('')}
                  </tr>
                `).join('')}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      <style>
        .section-title { margin: 0 0 var(--space-sm) 0; font-size: .95rem; }
        .roles-table { width: 100%; border-collapse: collapse; font-size: .85rem; }
        .roles-table th, .roles-table td {
          padding: var(--space-xs) var(--space-sm);
          border-bottom: 1px solid var(--color-surface-container);
          text-align: left;
        }
        .roles-table th { font-weight: 600; font-size: .8rem; color: var(--color-on-surface-muted); }
        .slot-cell { font-size: .8rem; color: var(--color-on-surface-muted); }
        .matrix-hint { font-size: .8rem; color: var(--color-on-surface-muted); margin: 0 0 var(--space-sm) 0; }
        .matrix-wrap { overflow-x: auto; }
        .compat-matrix { border-collapse: collapse; font-size: .8rem; }
        .compat-matrix th, .compat-matrix td {
          padding: 4px 6px;
          text-align: center;
          border: 1px solid var(--color-surface-container);
        }
        .matrix-header {
          writing-mode: vertical-lr;
          text-orientation: mixed;
          font-weight: 600;
          font-size: .7rem;
          max-width: 30px;
          white-space: nowrap;
        }
        .matrix-row-header { text-align: left; font-weight: 500; white-space: nowrap; padding-right: var(--space-sm); }
        .matrix-self { background: var(--color-surface-dim); color: var(--color-on-surface-muted); }
        .matrix-mirror { background: var(--color-surface-dim); }
        .matrix-cell { cursor: pointer; }
        .compat-check { cursor: pointer; }
      </style>
    `;

    // Auto-schedule toggle
    this.querySelectorAll('.auto-toggle').forEach(el => {
      el.addEventListener('change', (e) => {
        const input = e.target as HTMLInputElement;
        this.toggleAutoScheduled(input.dataset.roleSlug!, input.checked);
      });
    });

    // Compatibility matrix toggle
    this.querySelectorAll('.compat-check').forEach(el => {
      el.addEventListener('change', (e) => {
        const input = e.target as HTMLInputElement;
        const r1 = input.dataset.r1!;
        const r2 = input.dataset.r2!;
        this.toggleCompatibility(r1, r2, input.checked);
      });
    });
  }

  private slotLabel(slot: string): string {
    switch (slot) {
      case 'sabbath_school': return 'Szkoła sobotnia';
      case 'pre_sermon': return 'Przed kazaniem';
      case 'sermon': return 'Kazanie';
      case 'after': return 'Po nabożeństwie';
      default: return slot;
    }
  }

  private shortName(name: string): string {
    if (name.length <= 12) return name;
    return name.slice(0, 10) + '…';
  }

  private async toggleAutoScheduled(roleSlug: string, isAuto: boolean): Promise<void> {
    try {
      await gql.query(`
        mutation($slug: String!, $input: UpdateRoleInput!) {
          updateRole(slug: $slug, input: $input) { slug }
        }
      `, { slug: roleSlug, input: { isAutoScheduled: isAuto } });
      toast('Rola zaktualizowana', 'success');
    } catch (e) {
      toast(`Błąd: ${e instanceof Error ? e.message : e}`, 'error');
      this.loadData();
    }
  }

  private async toggleCompatibility(slug1: string, slug2: string, compatible: boolean): Promise<void> {
    try {
      await gql.query(`
        mutation($r1: String!, $r2: String!, $compat: Boolean!) {
          setRoleCompatibility(roleSlug1: $r1, roleSlug2: $r2, compatible: $compat)
        }
      `, { r1: slug1, r2: slug2, compat: compatible });

      // Update local data
      const [s1, s2] = [slug1, slug2].sort();
      if (compatible) {
        this.compatibilities.push({ roleSlug1: s1, roleSlug2: s2 });
      } else {
        this.compatibilities = this.compatibilities.filter(
          c => !(c.roleSlug1 === s1 && c.roleSlug2 === s2)
        );
      }
    } catch (e) {
      toast(`Błąd: ${e instanceof Error ? e.message : e}`, 'error');
      this.loadData();
    }
  }
}

customElements.define('roles-page', RolesPage);
